import { isSignal, Signal } from '@angular/core';
import { IWizardStep, StepState, StepStateEnum } from './wizard-step.model';
import { StepStateChangedEvent } from './wizard.events';

export function getTagsFromState(state: StepState) {
  return state === 0
    ? ['NONE']
    : Object.keys(StepStateEnum).filter(key => {
        const value = StepStateEnum[key];
        return (state & value) > 0;
      });
}

export function createState(...states: StepStateEnum[]): StepState {
  return states.reduce((acc, state) => acc | state, 0);
}

export function isFinished(state: StepState): boolean {
  return containsState(state, StepStateEnum.ENTERED) || containsState(state, StepStateEnum.SKIPPED);
}

export function containsState(state: StepState, contains: StepStateEnum): boolean {
  return (state & contains) > 0;
}

export function addState(state: StepState, add: StepStateEnum): StepState {
  return state | add;
}

export function removeState(state: StepState, remove: StepStateEnum): StepState {
  return state & ~remove;
}

type StepChecker = (state: StepState | Signal<StepState>) => boolean;
function createStepChecker(targetState: StepStateEnum): StepChecker {
  return function (state: StepState | Signal<StepState>) {
    const rawState = isSignal(state) ? state() : state;
    return containsState(rawState, targetState);
  };
}

type StepSetter = (state: StepState | Signal<StepState>, add?: boolean) => StepState;
function createStepSetter(targetState: StepStateEnum): StepSetter {
  return function (state: StepState | Signal<StepState>, add = true) {
    const rawState = isSignal(state) ? state() : state;
    return add ? addState(rawState, targetState) : removeState(rawState, targetState);
  };
}

/** Convenience function to check or set states */
export const isEntered = createStepChecker(StepStateEnum.ENTERED);
export const setEntered = createStepSetter(StepStateEnum.ENTERED);

export const isSkipped = createStepChecker(StepStateEnum.SKIPPED);
export const setSkipped = createStepSetter(StepStateEnum.SKIPPED);

export const isRevisiting = createStepChecker(StepStateEnum.REVISITING);
export const setRevisiting = createStepSetter(StepStateEnum.REVISITING);

export const isValid = createStepChecker(StepStateEnum.VALID);
export const setValid = createStepSetter(StepStateEnum.VALID);

export function withStepChangeEvent(step: IWizardStep, stepSetter: StepSetter, value = true) {
  const prevState = step.state();
  step.state.set(stepSetter(step.state, value));
  if (prevState !== step.state()) {
    step.context.events$.next(new StepStateChangedEvent(step, prevState, step.state()));
  }
}
